import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminListFaqs, adminCreateFaq, adminUpdateFaq, adminDeleteFaq } from "@/lib/apiClient";

const inputCls = "bg-[hsl(220_30%_8%)] border-border focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]";
const empty = { question: "", answer: "", category: "General" };

export default function FaqPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode, faq}
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    adminListFaqs().then(setItems).catch(() => toast.error("Failed to load FAQs.")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setForm(empty); setDialog({ mode: "create" }); };
  const openEdit = (faq) => { setForm({ question: faq.question, answer: faq.answer, category: faq.category || "General" }); setDialog({ mode: "edit", faq }); };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error("Question and answer are required."); return; }
    setSaving(true);
    try {
      if (dialog.mode === "create") { await adminCreateFaq(form); toast.success("FAQ added."); }
      else { await adminUpdateFaq(dialog.faq.id, form); toast.success("FAQ updated."); }
      setDialog(null); load();
    } catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await adminDeleteFaq(toDelete.id); toast.success("FAQ deleted."); load(); }
    catch { toast.error("Delete failed."); } finally { setToDelete(null); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">FAQs</h2>
          <p className="mt-1 text-sm text-muted-foreground">Manage the questions shown on the FAQ page.</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="faq-add-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add FAQ
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-3" data-testid="faq-admin-list">
          {items.map((f) => (
            <div key={f.id} className="flex items-start justify-between gap-4 rounded-[var(--radius-card)] border border-border bg-card p-5" data-testid={`faq-admin-item-${f.id}`}>
              <div className="flex items-start gap-3">
                <HelpCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{f.question}</p>
                    {f.category && <Badge variant="secondary" className="border-border bg-secondary text-xs text-muted-foreground">{f.category}</Badge>}
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.answer}</p>
                </div>
              </div>
              <div className="flex flex-shrink-0 gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(f)} data-testid={`faq-edit-button-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setToDelete(f)} data-testid={`faq-delete-button-${f.id}`} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="border-border bg-card" data-testid="faq-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode === "create" ? "Add FAQ" : "Edit FAQ"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-sm">Question</Label><Input value={form.question} onChange={set("question")} className={inputCls} data-testid="faq-question-input" /></div>
            <div className="space-y-2"><Label className="text-sm">Answer</Label><Textarea rows={4} value={form.answer} onChange={set("answer")} className={inputCls} data-testid="faq-answer-input" /></div>
            <div className="space-y-2"><Label className="text-sm">Category</Label><Input value={form.category} onChange={set("category")} className={inputCls} data-testid="faq-category-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="faq-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-border bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this FAQ?</AlertDialogTitle>
            <AlertDialogDescription>This will remove it from the public FAQ page. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="faq-confirm-delete-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
